<template>
  <teleport to="body">
    <div class="fixed inset-0 z-[2147483647] overflow-y-auto bg-slate-950/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="password-change-required-title">
      <div class="flex min-h-full items-center justify-center p-4 sm:p-6">
        <form class="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-900 sm:p-8" :dir="isRTL === true ? 'rtl' : 'ltr'" @submit.prevent="submitPasswordChange">
          <div class="mb-6">
            <div class="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 3h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2Zm3-10V6a3 3 0 1 1 6 0v4"></path>
              </svg>
            </div>
            <h2 id="password-change-required-title" class="text-2xl font-semibold text-gray-900 dark:text-white">{{ t('passwordChange.title', 'Password update required') }}</h2>
            <p class="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">{{ t('passwordChange.description', 'For your security, you must choose a new password before continuing.') }}</p>
          </div>

          <label class="mb-4 block">
            <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">{{ t('passwordChange.currentPassword', 'Current password') }}</span>
            <input v-model="currentPassword" type="password" autocomplete="current-password" class="input input-bordered w-full" :disabled="isSubmitting === true" required>
          </label>

          <label class="mb-4 block">
            <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">{{ t('passwordChange.newPassword', 'New password') }}</span>
            <input v-model="newPassword" type="password" autocomplete="new-password" class="input input-bordered w-full" :disabled="isSubmitting === true" required>
            <span class="mt-2 block text-xs text-gray-500 dark:text-gray-400">{{ t('passwordChange.passwordHint', 'Use at least 8 characters, including uppercase, lowercase, a number, and a symbol.') }}</span>
          </label>

          <label class="mb-5 block">
            <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">{{ t('passwordChange.confirmPassword', 'Confirm new password') }}</span>
            <input v-model="confirmPassword" type="password" autocomplete="new-password" class="input input-bordered w-full" :disabled="isSubmitting === true" required>
          </label>

          <p v-if="errorMessage !== ''" class="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/30 dark:text-red-300">{{ errorMessage }}</p>

          <div class="flex flex-col-reverse sm:flex-row sm:justify-end">
            <button type="button" class="btn mt-3 sm:mt-0" :disabled="isSubmitting === true" @click="$emit('logout')">{{ t('passwordChange.logout', 'Log out') }}</button>
            <button type="submit" class="btn sm:ml-3" :disabled="isSubmitting === true">
              <span v-if="isSubmitting === true" class="loading loading-spinner loading-sm"></span>
              {{ t('passwordChange.submit', 'Update password') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, ref } from 'vue';
import axios from '@/api/axiosInstance';
import { useI18n } from '@/i18n';

const emit = defineEmits(['logout', 'passwordChanged']);

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');
const isSubmitting = ref(false);
const { t, currentLanguage } = useI18n();
const isRTL = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

async function submitPasswordChange() {
  if (isSubmitting.value === true) {
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = t('passwordChange.passwordMismatch', 'The new passwords do not match.');
    return;
  }

  errorMessage.value = '';
  isSubmitting.value = true;
  try {
    await axios.post('/api/users/changePassword', {
      currentPassword: currentPassword.value,
      newPassword: newPassword.value
    });
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
    isSubmitting.value = false;
    emit('passwordChanged');
  } catch (error) {
    const responseError = error !== null && error !== undefined && error.response !== undefined && error.response !== null ? error.response.data : null;
    const message = responseError !== null && responseError !== undefined && typeof responseError.error === 'string' ? responseError.error : '';
    errorMessage.value = message !== '' ? message : t('passwordChange.failed', 'Unable to update your password. Please try again.');
    isSubmitting.value = false;
  }
}
</script>
